import { GameState, IMove, IPiece, Position } from "../../types";
import { MCTSUtils_v2, cloneFullGameState } from "../utils/MCTSUtils";
import { MCTSMoveGenerationService } from "../utils/MCTSMoveGenerationService";
import { MoveInfo } from "../decision-engine/interfaces/DecisionPriority";
import { ZodiacEngine } from "../../zodiacEngine";
import { MCTSEvaluation } from "../evaluation/MCTSEvaluation";

/**
 * Focused MCTS Implementation
 *
 * This is a streamlined MCTS designed specifically for searching filtered moves
 * with emphasis on:
 * - Material balance (capturing vs losing pieces)
 * - Formation advancement (supporting pieces, not just racing)
 * - Defensive positioning over pure speed
 *
 * Can be easily toggled on/off via configuration.
 */

interface MCTSNode {
  gameState: GameState;
  move: MoveInfo | null; // The move that led to this state
  parent: MCTSNode | null;
  children: MCTSNode[];
  visits: number;
  wins: number;
  isTerminal: boolean;
  untriedMoves: MoveInfo[];
  priorProbabilities: Map<string, number>; // Move ID -> Tactical Prior Score
}

interface MCTSConfig {
  maxIterations: number;
  maxDepth: number;
  explorationConstant: number; // UCB1 C parameter
  materialWeight: number;
  formationWeight: number;
  advancementWeight: number;
  entanglementWeight: number; // Weight for entanglement evaluation
}

export class FocusedMCTS {
  private config: MCTSConfig;
  private gameEngine: ZodiacEngine;
  private debugMode: boolean = false;
  private analysisLogs: string[] = [];
  private evaluator: MCTSEvaluation; // Modular evaluator

  constructor(gameEngine: ZodiacEngine, config?: Partial<MCTSConfig>) {
    this.gameEngine = gameEngine;
    this.config = {
      maxIterations: 10000,
      maxDepth: 8,
      explorationConstant: 1.4,
      materialWeight: 100,
      formationWeight: 50,
      advancementWeight: 25,
      entanglementWeight: 10, // Default entanglement weight
      ...config,
    };
    this.evaluator = new MCTSEvaluation(this.config); // Init evaluator
  }

  /**
   * Main MCTS search function
   * Searches only the provided filtered moves
   */
  public search(
    rootState: GameState,
    filteredMoves: MoveInfo[]
  ): { bestMove: MoveInfo; analysisData: any } {
    this.analysisLogs = [];
    this.addLog(
      `🔍 Starting Focused MCTS with ${filteredMoves.length} filtered moves`
    );
    this.addLog(
      `📊 Config: ${this.config.maxIterations} iterations, depth ${this.config.maxDepth}`
    );

    // Create root node with filtered moves as untried moves
    const rootNode: MCTSNode = {
      gameState: rootState,
      move: null,
      parent: null,
      children: [],
      visits: 0,
      wins: 0,
      isTerminal: false,
      untriedMoves: [...filteredMoves],
      priorProbabilities: new Map<string, number>(),
    };

    const startTime = Date.now();

    // Run MCTS iterations
    for (let i = 0; i < this.config.maxIterations; i++) {
      this.mctsIteration(rootNode);
    }

    const elapsedTime = Date.now() - startTime;
    this.addLog(`⏱️ MCTS completed in ${elapsedTime}ms`);

    // Select best move based on most visits (robust selection)
    const bestChild = this.selectBestChild(rootNode);
    const bestMove = bestChild?.move || filteredMoves[0];

    // Generate analysis data
    const analysisData = this.generateAnalysisData(rootNode, elapsedTime);

    this.addLog(
      `🏆 Best move: [${bestMove.fromPosition.row},${bestMove.fromPosition.col}] → [${bestMove.move.position.row},${bestMove.move.position.col}]`
    );
    this.addLog(
      `📈 Visits: ${bestChild?.visits || 0}, Win Rate: ${
        bestChild ? ((bestChild.wins / bestChild.visits) * 100).toFixed(1) : 0
      }%`
    );

    return {
      bestMove,
      analysisData,
    };
  }

  /**
   * Single MCTS iteration: Selection → Expansion → Simulation → Backpropagation
   */
  private mctsIteration(rootNode: MCTSNode): void {
    // 1. Selection: Select a node to expand
    const selectedNode = this.selection(rootNode);

    // 2. Expansion: Add a child node
    const expandedNode = this.expansion(selectedNode);

    // 3. Simulation: Play out the game
    const simulationResult = this.simulation(expandedNode);

    // 4. Backpropagation: Update statistics
    this.backpropagation(expandedNode, simulationResult);
  }

  /**
   * Selection phase: Use UCB1 to select the best node to expand
   */
  private selection(node: MCTSNode): MCTSNode {
    let current = node;

    while (current.untriedMoves.length === 0 && current.children.length > 0) {
      current = this.selectChildByUCB1(current);
    }

    return current;
  }

  /**
   * Expansion phase: Add a new child node
   */
  private expansion(node: MCTSNode): MCTSNode {
    if (node.untriedMoves.length === 0) {
      return node; // Fully expanded or terminal
    }

    // Select a random untried move
    const randomIndex = Math.floor(Math.random() * node.untriedMoves.length);
    const moveToTry = node.untriedMoves.splice(randomIndex, 1)[0];

    // Create new game state by applying the move
    const newState = this.applyMove(node.gameState, moveToTry);

    // Generate possible moves for the new state
    const possibleMoves = this.generateMoves(newState);

    // Create child node
    const childNode: MCTSNode = {
      gameState: newState,
      move: moveToTry,
      parent: node,
      children: [],
      visits: 0,
      wins: 0,
      isTerminal: this.isTerminal(newState),
      untriedMoves: possibleMoves,
      priorProbabilities: new Map<string, number>(),
    };

    node.children.push(childNode);
    return childNode;
  }

  /**
   * Simulation phase: Play out the game to completion or max depth
   */
  private simulation(node: MCTSNode): number {
    if (node.isTerminal) {
      return this.evaluateTerminalState(node.gameState);
    }

    let currentState = cloneFullGameState(node.gameState);
    let depth = 0;

    while (depth < this.config.maxDepth && !this.isTerminal(currentState)) {
      const moves = this.generateMoves(currentState);
      if (moves.length === 0) break;

      // Select a move using focused evaluation
      const selectedMove = this.selectSimulationMove(currentState, moves);
      currentState = this.applyMove(currentState, selectedMove);
      depth++;
    }

    return this.evaluator.evaluatePosition(
      currentState,
      !currentState.isLightTurn
    ); // Use modular eval
  }

  /**
   * Backpropagation phase: Update node statistics
   */
  private backpropagation(node: MCTSNode, result: number): void {
    let current: MCTSNode | null = node;

    while (current !== null) {
      current.visits++;
      current.wins += result;
      current = current.parent;
    }
  }

  /**
   * PUCT selection for choosing best child (AlphaZero-style with tactical priors)
   */
  private selectChildByUCB1(node: MCTSNode): MCTSNode {
    let bestChild = node.children[0];
    let bestValue = -Infinity;

    for (const child of node.children) {
      const exploitation = child.visits > 0 ? child.wins / child.visits : 0;

      // Get tactical prior for this move
      const moveId = this.getMoveId(child.move);
      const tacticalPrior = node.priorProbabilities.get(moveId) || 0.1; // Default prior

      // PUCT formula: Q(s,a) + C * P(s,a) * sqrt(N(s)) / (1 + N(s,a))
      const exploration =
        (Math.sqrt(node.visits) / (1 + child.visits)) * tacticalPrior;

      const puctValue =
        exploitation + this.config.explorationConstant * exploration;

      if (puctValue > bestValue) {
        bestValue = puctValue;
        bestChild = child;
      }
    }

    return bestChild;
  }

  /**
   * Select best child based on most visits (robust selection)
   */
  private selectBestChild(node: MCTSNode): MCTSNode | null {
    if (node.children.length === 0) return null;

    return node.children.reduce((best, child) =>
      child.visits > best.visits ? child : best
    );
  }

  /**
   * Apply a move to create a new game state
   */
  private applyMove(state: GameState, move: MoveInfo): GameState {
    const newState = cloneFullGameState(state);
    // Apply the move using the game engine's move application logic
    // This is a simplified version - in practice, you'd use the full move handler
    const piece = MCTSUtils_v2.getPieceAt(newState.board, move.fromPosition);
    if (piece) {
      // Remove piece from old position
      newState.board[move.fromPosition.row][move.fromPosition.col].Pieces = [];
      // Add piece to new position
      newState.board[move.move.position.row][move.move.position.col].Pieces = [
        piece,
      ];
      // Flip turn
      newState.isLightTurn = !newState.isLightTurn;
    }
    return newState;
  }

  /**
   * Generate possible moves for a game state
   */
  private generateMoves(state: GameState): MoveInfo[] {
    const moveService = MCTSMoveGenerationService.getInstance();
    return moveService.getAllMoves(state);
  }

  /**
   * Check if a state is terminal (game over)
   */
  private isTerminal(state: GameState): boolean {
    // Check for race victory
    const isLight = state.isLightTurn;
    const victoryRow = isLight ? 7 : 0;

    // Check if any king reached victory row
    for (let col = 0; col < 5; col++) {
      const pieces = state.board[victoryRow][col].Pieces;
      for (const piece of pieces) {
        if (piece.isKing) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Evaluate a terminal state
   */
  private evaluateTerminalState(state: GameState): number {
    return this.evaluator.evaluatePosition(state, !state.isLightTurn); // Use eval for consistency
  }

  /**
   * Select a move during simulation using focused evaluation
   */
  private selectSimulationMove(state: GameState, moves: MoveInfo[]): MoveInfo {
    // Use a simplified version of the evaluation to select moves quickly
    let bestMove = moves[0];
    let bestScore = -Infinity;

    for (const move of moves.slice(0, Math.min(5, moves.length))) {
      // Limit to 5 moves for speed
      const newState = this.applyMove(state, move);
      const score = this.evaluator.evaluatePosition(
        newState,
        !state.isLightTurn
      ); // Use modular eval

      if (score > bestScore) {
        bestScore = score;
        bestMove = move;
      }
    }

    return bestMove;
  }

  /**
   * Generate analysis data for UI
   */
  private generateAnalysisData(rootNode: MCTSNode, elapsedTime: number): any {
    const topMoves = rootNode.children
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 5)
      .map((child) => ({
        move: child.move,
        visits: child.visits,
        wins: child.wins,
        winRate: child.wins / child.visits,
        ucb1Score: child.visits > 0 ? child.wins / child.visits : 0,
      }));

    return {
      iterations: this.config.maxIterations,
      nodesVisited: this.countTotalNodes(rootNode),
      executionTime: elapsedTime,
      topMoves,
      logs: this.analysisLogs,
    };
  }

  /**
   * Count total nodes in the tree
   */
  private countTotalNodes(node: MCTSNode): number {
    let count = 1;
    for (const child of node.children) {
      count += this.countTotalNodes(child);
    }
    return count;
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
    if (this.debugMode) {
    }
  }

  public setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  public updateConfig(config: Partial<MCTSConfig>): void {
    this.config = { ...this.config, ...config };
    this.evaluator = new MCTSEvaluation(this.config); // Re-init eval with new config
  }

  /**
   * Generate consistent move ID for prior probability lookup
   */
  private getMoveId(move: MoveInfo | null): string {
    if (!move) return "null";
    return `${move.fromPosition.row},${move.fromPosition.col}->${move.move.position.row},${move.move.position.col}`;
  }

  /**
   * Set tactical priors for a node based on priority system analysis
   */
  public setTacticalPriors(
    node: MCTSNode,
    tacticalScores: Record<string, number>
  ): void {
    node.priorProbabilities.clear();

    // Set priors from tactical analysis
    for (const [moveId, score] of Object.entries(tacticalScores)) {
      // Convert tactical score (0-1) to prior probability (0.1-0.9)
      const prior = Math.max(0.1, Math.min(0.9, 0.1 + score * 0.8));
      node.priorProbabilities.set(moveId, prior);
    }

    // Set default priors for moves without tactical scores
    for (const move of node.untriedMoves) {
      const moveId = this.getMoveId(move);
      if (!node.priorProbabilities.has(moveId)) {
        node.priorProbabilities.set(moveId, 0.1); // Default prior
      }
    }

    this.addLog(
      `🎯 Set tactical priors for ${Object.keys(tacticalScores).length} moves`
    );
  }
}
